/* eslint-disable max-lines */
import clsx from "clsx"
import { FC } from "react"
import styles from "./TermsOfUseContent.module.scss"

export const TermsOfUseContent: FC = () => {
  return (
    <div className={clsx(styles.TermsOfUseContent)}>
      <p className={clsx(styles.TermsOfUseContent_lastUpdated)}>
        <i>Last updated on March 17, 2026</i>
      </p>
      <h1>Terms of Use</h1>

      <h3>1. INTRODUCTION</h3>
      <p>
        These Terms of Use (“<b>Terms of Use</b>”, or “<b>Terms</b>”) form a
        legally binding agreement between User (“<b>You</b>”, “<b>User</b>” or “
        <b>Player</b>”) and Developers (“<b>Developers</b>”, “<b>We</b>
        ”, or “<b>Us</b>”).
      </p>
      <p>
        The Terms of Use have been developed by Us to establish the rules for
        the use of and access to the Card Game <b>“Clash of Diggers”</b> to
        Users of the Service and any other Services provided by Our Developers
        and apply to all Our Users without exception. These Terms constitute a
        mutual agreement between Developers and You for Your use of Our Game
        Services (<b>“Service”</b>).
      </p>
      <p>
        You should read these Terms carefully before You start using any of Our
        Services. If You start using Our Services and features, You
        automatically confirm that You have read, understand, accept and will
        comply with these Terms and agree to be bound by them, as well as the
        provisions of Our Anti-Money Laundering and Counter-Terrorist Financing
        Policy and You assume all associated risks and responsibilities.
      </p>
      <p>You agree to use Our Service only for lawful purposes.</p>

      <h3>2. DEFINITIONS</h3>
      <p>
        The terms defined below used in these Terms have the following meanings:
      </p>
      <ul>
        <li>
          “<b>Account</b>” means a personal record to identify each User issued
          by the Developers at the time when the Service starts to be used.
        </li>
        <li>
          “<b>Content</b>” means text, sounds, music, images, videos, software,
          programs, computer code and other information.
        </li>
        <li>
          <b>“Clash of Diggers”</b> (the <b>“Card Game”</b> or <b>“the Game”</b>
          ) means the digital card-based game operated by the Developers and
          accessible via the Platform, including through a dedicated Telegram
          Bot and the web version available at{" "}
          <a
            href="https://app.clashofdiggers.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://app.clashofdiggers.com
          </a>{" "}
          (the “<b>Website</b>”), in which Users participate in
          player-versus-player matches using digital cards with various
          abilities, and may obtain or purchase certain in-game items such as
          cards, cosmetic skins, emoji packs, or other digital content related
          to gameplay.
        </li>
        <li>
          <b>“Marks”</b> means logo, as well as any other products, service
          names, slogans, trademarks and service marks associated with the
          brand.
        </li>
        <li>
          “<b>Fee</b>” means a commission or other costs, charged by the
          Developers for the use of Services.
        </li>
        <li>
          “<b>Force Majeure</b>” means unforeseen circumstances beyond the
          reasonable control of the Developers and the Users that make it
          impossible or difficult for the Developers and the Users to fulfill
          their obligations. The list of such circumstances is described in
          Section 6 of these Terms.
        </li>
        <li>
          “<b>Law</b>” or “<b>Applicable Law</b>” means all applicable laws,
          regulations, decrees, decisions, resolutions, orders, etc., of the
          state authorities of the British Virgin Islands.
        </li>
        <li>
          <b>“Telegram Bot”</b> means a software-based automated tool or script
          that interacts with Users via the Telegram Messenger platform,
          performing predefined tasks, collecting User input, and providing
          services or information as instructed by its developers or
          administrators. The Telegram Bot acts as an interface between the
          Developers and the User within the scope of the Telegram
          infrastructure;
        </li>
        <li>
          <b>“Platform”</b> means the digital environment through which the Card
          Game and Services are made available to Users, including the Telegram
          Bot, the Website, and any related interfaces, applications, or
          integrations through which Users may access the Card Game.
        </li>
        <li>
          “<b>Privacy Policy</b>” means rules of personal data collection,
          storage, and use, developed following the applicable law.
        </li>
        <li>
          “<b>Prohibited Countries</b>” means a country or territory that is
          subject to sanctions administered or applied by any sanctions
          authority, as well as countries where special licenses are required.
        </li>
        <li>
          “<b>Politically exposed persons</b>”(“<b>PEP</b>”) shall mean natural
          persons who are or who have been entrusted with prominent public
          functions as well as their family members or close associates of such
          persons.
        </li>
        <li>
          <b>“Services”</b> means all and any Services provided by the
          Developers through the Platform.
        </li>
        <li>
          “<b>Submitted Content</b>” means any content that Users have
          submitted, transmitted or uploaded on or to the Service.
        </li>
        <li>
          <b>“Virtual assets”</b> means a digital representation of value that
          can be digitally traded or transferred, and can be used for payment or
          investment purposes.
        </li>
        <li>
          <b>“Players” or “User(s)” or “You”</b> means individuals who access or
          participate in the Card Game through the Platform and use the Services
          provided by the Developers.
        </li>
      </ul>

      <h3>3. PLATFORM DESCRIPTION</h3>
      <p>
        3.1. The Card Game is a digital interactive game developed and operated
        by the Developers as a separate gameplay product. The Card Game provides
        Users with access to a strategic card-based gameplay experience where
        Users compete against other players using digital cards with various
        abilities and attributes.
      </p>
      <p>
        3.2. The Card Game is accessible through the Platform, including via a
        dedicated Telegram Bot, the web version available at:{" "}
        <a
          href="https://app.clashofdiggers.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://app.clashofdiggers.com
        </a>
        , and may also be accessed via the game “Labrador Adventure” (
        <a
          href="https://t.me/LabradorAdventuresBot"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://t.me/LabradorAdventuresBot
        </a>
        ) through a link or interface element that redirects the User to the
        Card Game environment. The Card Game constitutes a separate digital
        product, and its use is governed exclusively by these Terms, unless
        expressly stated otherwise.
      </p>
      <p>
        3.3. The core gameplay of the Card Game is based on the use of digital
        cards that Users may use during matches against other players.
      </p>
      <p>
        3.4. Additional Cards represent digital in-game items that may contain
        different gameplay attributes, abilities, or effects that may influence
        the outcome of a match.
      </p>
      <p>
        3.5. Additional cards may be obtained by Users through gameplay
        progression, participation in events, in-game rewards, or through
        purchases made in the in-game store.
      </p>
      <p>
        3.6. Certain cards may provide conditional gameplay advantages depending
        on their abilities or attributes. The Developers reserve the right to
        modify, rebalance, replace, or remove cards and their gameplay effects
        at any time in order to maintain fair gameplay and game balance.
      </p>
      <p>
        3.7. During gameplay the game interface may display various gameplay
        parameters, statistics, and resources available to the User during a
        match. Such parameters may influence gameplay decisions and match
        outcomes.
      </p>
      <p>
        3.8. The Card Game includes a PvP gameplay system where Users may
        compete against other players in real-time matches using their cards.
      </p>
      <p>
        3.9. The Card Game may include gameplay modes where Users are matched
        with other players and participate in matches according to the rules
        implemented in the game.
      </p>
      <p>
        3.10. The Card Game includes a gameplay mode where Users voluntarily
        participate in matches involving token-based stakes denominated in TON
        cryptocurrency.
      </p>
      <p>
        3.11. In such gameplay mode Users may select a match lobby corresponding
        to a specific stake level and join a match with another player who has
        selected the same lobby.
      </p>
      <p>
        3.12. In matches involving token-based stakes each participating User
        contributes an equal amount of TON tokens before the match begins.
      </p>
      <p>
        3.13. The match pool is formed by combining the stakes contributed by
        both participating players.
      </p>
      <p>
        3.14. The match will begin only after both participating Users confirm
        their participation.
      </p>
      <p>
        3.15. If one of the Users does not confirm participation, the match will
        be cancelled and no funds will be deducted from the Users.
      </p>
      <p>
        3.16. Funds are debited from participating Users only after both players
        confirm the match.
      </p>
      <p>
        3.17. After the match concludes the winning User receives the total
        match pool minus a commission retained by the Developers.
      </p>
      <p>
        3.18. The commission charged by the Developers for such matches is ten
        percent (10%) of the total match pool.
      </p>
      <p>
        3.19. Participation in token-based matches is voluntary and Users
        acknowledge that match outcomes are uncertain and that they may lose the
        tokens used to enter such matches.
      </p>
      <p>
        3.20. While waiting for an opponent or match confirmation, certain
        functions of the application may remain available to the User, however
        additional matches may be temporarily unavailable until the current
        match status is resolved.
      </p>
      <p>
        3.21. The Card Game includes an in-game store where Users may obtain
        digital items related to gameplay.
      </p>
      <p>
        3.22. Items available in the store may include digital cards, cosmetic
        skins, emoji packs, visual customization elements, and other digital
        items associated with the Card Game.
      </p>
      <p>
        3.23. Cosmetic items such as skins or emojis modify the visual
        appearance of certain elements within the Card Game and do not directly
        influence gameplay mechanics.
      </p>
      <p>
        3.24. Certain cards available in the store may contain gameplay
        abilities or effects that may provide conditional advantages during
        matches.
      </p>
      <p>
        3.25. Some cards may perform specific gameplay actions such as modifying
        gameplay parameters, removing negative effects, temporarily influencing
        gameplay conditions, or triggering other in-game effects during matches.
      </p>
      <p>
        3.26. Items in the store may be offered for purchase using supported
        payment methods including TON cryptocurrency, Telegram Stars, or other
        payment methods supported by the Telegram platform.
      </p>
      <p>
        3.27. Certain cards or items may also be obtained without payment
        through gameplay progression, in-game rewards, events, or other reward
        mechanisms implemented by the Developers.
      </p>
      <p>
        3.28. The availability, characteristics, pricing, and gameplay effects
        of cards or other items offered in the store may change from time to
        time at the discretion of the Developers.
      </p>
      <p>
        3.29. The Card Game operates using Telegram Bot technology and relies on
        the technical infrastructure of the Telegram platform.
      </p>
      <p>
        3.30. Access to the Card Game therefore requires a Telegram account and
        certain functionality of the Card Game may depend on Telegram services
        and platform availability.
      </p>
      <p>
        3.31. The Developers are not responsible for interruptions, limitations,
        or technical issues arising from the operation of the Telegram platform
        or other external infrastructure.
      </p>
      <p>
        3.32. The Developers may introduce additional gameplay mechanics, cards,
        tournaments, events, leaderboards, seasonal content, or other features
        in the Card Game as the product continues to evolve.
      </p>
      <p>
        3.33. The Developers reserve the right to modify, expand, restrict,
        rebalance, or discontinue any functionality of the Card Game at any time
        in order to improve gameplay, maintain system stability, or comply with
        legal or technical requirements.
      </p>

      <h3>4. PROHIBITED ACTIVITIES</h3>
      <p>
        4.1. Users are strictly prohibited from using the Services or the
        Platform in connection with any activities that the Developers, in their
        sole discretion, deem unlawful, abusive, unethical, fraudulent,
        manipulative, or otherwise harmful to the Card Game, its Users, or the
        Developers (the <b>“Prohibited Activities”</b>).
      </p>
      <p>
        4.2. Users are advised to contact the Developers in case of any doubt
        regarding the permissibility of their intended use of the Card Game or
        any related functionality.
      </p>
      <p>4.3. The list below is illustrative and non-exhaustive.</p>
      <p>
        4.3.1. Multi-accounting and account abuse. Creating, controlling, or
        using more than one account for the purpose of obtaining an unfair
        advantage in the Card Game, circumventing gameplay restrictions,
        manipulating matchmaking, abusing rewards, or otherwise interfering with
        the normal operation of the Card Game is prohibited.
      </p>
      <p>
        4.3.2. Use of bots, scripts, automation, or unauthorized software. Using
        bots, scripts, macros, emulators, auto-clickers, software tools,
        modified clients, or any automated or semi-automated means to access,
        play, control, or interact with the Card Game is prohibited where such
        use gives the User an advantage, automates gameplay, or interferes with
        normal game operation.
      </p>
      <p>
        4.3.3. Circumvention of game mechanics and exploitation of bugs. Using
        bugs, errors, exploits, vulnerabilities, unintended mechanics, or
        loopholes in the Card Game to gain an advantage, manipulate gameplay
        outcomes, avoid payment, duplicate cards or items, or otherwise benefit
        in a manner not intended by the Developers is prohibited. Users must
        promptly report any such issue discovered by them to the Developers.
      </p>
      <p>
        4.3.4. Unauthorized interference with the Card Game or the Platform. Any
        attempt to interfere with the Card Game, its servers, Telegram Bot
        infrastructure, payment flows, matchmaking logic, store functionality,
        or technical operation is prohibited. This includes attempts to hack the
        Card Game, overload its infrastructure, gain unauthorized access to
        accounts or systems, bypass technical protections, or alter the normal
        functioning of the game.
      </p>
      <p>
        4.3.5. Fraudulent conduct and deception. Engaging in fraud, deception,
        misrepresentation, or manipulative conduct in connection with the Card
        Game, its store, or TON-based matches is prohibited. This includes,
        without limitation:
      </p>
      <ul>
        <li>providing false or misleading information;</li>
        <li>using false identities or unauthorized credentials;</li>
        <li>
          attempting to mislead other Users or the Developers regarding
          gameplay, transactions, or match participation;
        </li>
        <li>abusing payment flows or transaction mechanisms;</li>
        <li>
          attempting to obtain cards, items, or gameplay outcomes through
          dishonest means.
        </li>
      </ul>
      <p>
        4.3.6. Manipulation of gameplay outcomes. Any attempt to manipulate the
        result of a card match, including through collusion, match-fixing,
        coordinated surrender, use of multiple accounts, arranged losses, or any
        other artificial conduct intended to distort fair gameplay or TON-based
        match outcomes, is prohibited.
      </p>
      <p>
        4.3.7. Abuse of TON-based match functionality. Where the Card Game
        offers player-versus-player matches involving TON-based stakes, it is
        prohibited to:
      </p>
      <ul>
        <li>
          enter such matches without genuine intent to participate fairly;
        </li>
        <li>manipulate match confirmations or cancellations;</li>
        <li>
          collude with another User to transfer value through staged matches;
        </li>
        <li>
          use multiple accounts or coordinated conduct to influence TON-based
          match outcomes;
        </li>
        <li>
          exploit technical errors related to stake placement, match
          confirmation, or prize distribution.
        </li>
      </ul>
      <p>
        4.3.8. Abuse of closed beta access. If the Card Game operates in a
        closed beta phase, Users may not abuse beta access, circumvent access
        limitations, share access in an unauthorized manner, attempt to bypass
        beta restrictions, or exploit incomplete or test functionality in bad
        faith.
      </p>
      <p>
        4.3.9. Improper use of in-game items. Users may not use cards, skins,
        emojis, or other digital items in any manner contrary to these Terms,
        the intended gameplay purpose of such items, or the technical
        restrictions implemented by the Developers. Any attempt to duplicate,
        falsify, reverse engineer, externally commercialize, or misuse such
        items is prohibited.
      </p>
      <p>
        4.3.10. Unauthorized sale or transfer of accounts or access. The User’s
        account and access rights to the Card Game are personal. Selling,
        purchasing, renting, leasing, transferring, sublicensing, or otherwise
        assigning accounts or access rights to third parties is prohibited.
      </p>
      <p>
        4.3.11. Impersonation and false affiliation. Creating communities,
        groups, channels, pages, bots, or accounts in a manner that may mislead
        others into believing that such resources are official or affiliated
        with the Developers is prohibited. Users may not use the Developers’
        name, trademarks, branding, or other project identifiers without prior
        written permission in a manner likely to confuse or misrepresent
        affiliation.
      </p>
      <p>
        4.3.12. Toxic behavior and abuse. Users may not insult, harass,
        threaten, humiliate, blackmail, or otherwise abuse other Users or
        representatives of the Developers.
      </p>
      <p>
        4.3.13. Illegal or unregulated activities. Users may not use the Card
        Game or its infrastructure in connection with illegal commercial
        activities, prohibited goods or services, or unlawful transactions in
        violation of applicable law.
      </p>
      <p>
        4.3.14. Money laundering, sanctions, and terrorist financing. The
        Developers maintain a strict zero-tolerance policy against any
        activities that may facilitate money laundering, terrorist financing, or
        sanctions evasion. This includes using the Card Game or TON-based match
        functionality in connection with criminal proceeds or engaging with
        persons or jurisdictions subject to applicable sanctions.
      </p>
      <p>
        4.3.15. Intellectual property infringement. Users may not violate the
        intellectual property rights of the Developers or third parties in
        connection with the Card Game. This includes unauthorized copying,
        reproduction, modification, reverse engineering, extraction of game
        assets, or misuse of the Developers’ name, branding, visuals, or
        content.
      </p>
      <p>
        4.3.16. Abuse of platform infrastructure. Users may not introduce
        viruses, malicious code, or other disruptive technologies into the Card
        Game or related systems, nor attempt to scrape, crawl, overload, or
        otherwise interfere with the Card Game infrastructure.
      </p>
      <p>
        4.3.17. Minors and incapable persons. The Card Game may not be used by
        persons under 18 years of age or by persons legally incapable of
        entering into binding agreements under applicable law. If the Developers
        detect or reasonably suspect such use, they reserve the right to
        immediately suspend or terminate access to the Card Game.
      </p>
      <p>
        4.4. The above list of prohibited actions is not exhaustive. Any conduct
        not expressly listed but similar in nature to cheating, fraud, abuse,
        unlawful activity, or interference with the Card Game may also be
        treated as a violation of these Terms.
      </p>
      <p>
        4.5. The Developers reserve the right to investigate any suspected
        breach of this Section. Where such breach is confirmed or reasonably
        suspected, the Developers may, at their sole discretion:
      </p>
      <ul>
        <li>temporarily suspend access to the Card Game;</li>
        <li>restrict access to certain gameplay modes or features;</li>
        <li>
          cancel or reverse the results of affected matches or transactions;
        </li>
        <li>
          remove cards, items, or other digital assets obtained in violation of
          these Terms;
        </li>
        <li>block or permanently terminate the User’s account;</li>
        <li>refuse to process transactions;</li>
        <li>
          report information to competent authorities where required by law;
        </li>
        <li>take legal action to recover damages or protect their rights.</li>
      </ul>
      <p>
        4.6. If an account is suspended, blocked, or terminated due to a
        violation of these Terms, the User shall not be entitled to any refund
        of amounts paid, including payments made in TON, Telegram Stars, or for
        any digital items purchased in the Card Game.
      </p>
      <p>
        4.7. The Developers shall not be liable for any loss or damage suffered
        by the User as a result of enforcement measures taken under this Section
        where such measures are applied in accordance with these Terms.
      </p>

      <h3>5. FEES</h3>
      <p>
        5.1. The Developers are committed to transparency in matters relating to
        fees and payments within the Card Game. Users will be provided with
        information regarding applicable fees, costs, or commissions before
        initiating any transaction or participating in paid gameplay features.
      </p>
      <p>
        5.2. The Card Game may offer digital items, including cards, cosmetic
        skins, emoji packs, or other in-game content, which may be available for
        purchase through the in-game store.
      </p>
      <p>
        5.3. Purchases within the Card Game store may be made using supported
        payment methods available through the Platform, including TON
        cryptocurrency or Telegram Stars, depending on the options available at
        the time of purchase.
      </p>
      <p>
        5.4. Gameplay mode involves voluntary participation in
        player-versus-player matches that include token-based stakes denominated
        in TON.
      </p>
      <p>
        5.5. In such matches, each participating User contributes an equal
        amount of TON tokens before the match begins, forming the total match
        pool.
      </p>
      <p>
        5.6. After the match concludes, the winning User receives the total
        match pool minus a commission retained by the Developers.
      </p>
      <p>
        5.7. The commission charged by the Developers for such matches is ten
        percent (10%) of the total match pool.
      </p>
      <p>
        5.8. The Developers reserve the right to modify applicable fees,
        introduce new fee structures, or adjust existing commissions in
        connection with the operation of the Card Game. Any such changes will
        apply to transactions or gameplay features introduced after such changes
        become effective.
      </p>
      <p>
        5.9. Material changes to the Terms or applicable fee structures may be
        announced through the Platform interface, the Telegram Bot, official
        communication channels, or other means designated by the Developers.
        Unless otherwise stated, such changes will take effect upon their
        publication.
      </p>

      <h3>6. FORCE MAJEURE</h3>
      <p>
        6.1. The Developers shall not be held liable for any delay or failure in
        fulfilling its obligations under these Terms due to events beyond its
        reasonable control (<b>“Force Majeure Event”</b>). Such events may
        include, without limitation: natural disasters or extreme weather
        conditions; acts of God; war, armed conflict, terrorism, or civil
        disturbances; outbreaks of epidemics or pandemics; governmental or
        regulatory restrictions or directives; labor strikes, industrial
        disputes, or disruptions in supply chains; technical malfunctions, cyber
        incidents, or interruptions to internet or blockchain infrastructure,
        etc.
      </p>
      <p>
        6.2. During the occurrence of a Force Majeure Event, the Developers’
        obligations shall be deemed suspended for the duration of such event.
        The Developers will use reasonable efforts to restore normal operations
        at the earliest opportunity.
      </p>
      <p>
        6.3. Users acknowledge and accept that any delays, interruptions, or
        unavailability of the Services resulting from a Force Majeure Event
        shall not give rise to any right to compensation, reimbursement, or
        legal claims against the Developers.
      </p>

      <h3>7. DISCLAIMERS AND LIMITATIONS OF LIABILITY</h3>
      <p>
        7.1. The Card Game is provided on an “as is” and “as available” basis.
        The Developers do not provide any warranty, condition, or representation
        regarding the accuracy, reliability, availability, or fitness of the
        Card Game for any particular purpose.
      </p>
      <p>
        7.2. The Developers do not guarantee that the Card Game will operate
        without interruptions, errors, delays, or technical defects. The Card
        Game relies on third-party technologies and infrastructure, including
        the Telegram platform, which may affect the availability or
        functionality of the Services.
      </p>
      <p>
        7.3. Users acknowledge and agree that the Developers have no control
        over and assume no responsibility for:
      </p>
      <ul>
        <li>
          failures of hardware, software, or internet connections used by the
          User;
        </li>
        <li>
          technical problems related to the Telegram platform or its
          infrastructure;
        </li>
        <li>
          malicious software or unauthorized access affecting the User’s device
          or account;
        </li>
        <li>
          other technical issues or circumstances beyond the Developers’
          reasonable control.
        </li>
      </ul>
      <p>
        7.4. The User understands and agrees that participation in the Card Game
        involves certain risks and that the Card Game is used entirely at the
        User’s own risk.
      </p>
      <p>
        7.5. The Developers shall not be liable for any losses, damages, or
        claims arising from:
      </p>
      <ul>
        <li>
          errors made by the User, including incorrect transactions or gameplay
          decisions;
        </li>
        <li>loss of access to the User’s account;</li>
        <li>technical failures or interruptions of the Card Game;</li>
        <li>software errors or bugs affecting gameplay;</li>
        <li>actions or misconduct of other Users.</li>
      </ul>
      <p>
        7.6. To the maximum extent permitted by applicable law, the Developers
        shall not be liable for any direct, indirect, incidental, special, or
        consequential damages arising from the use of or inability to use the
        Card Game or related Services.
      </p>
      <p>
        7.7. The Developers are not responsible for the temporary unavailability
        of the Telegram Bot, the Card Game, or any related features, including
        interruptions caused by technical maintenance, software updates, or
        external service failures.
      </p>
      <p>
        7.8. The Developers reserve the right to modify, rebalance, replace, or
        remove cards, gameplay parameters, abilities, or other game mechanics at
        any time in order to maintain gameplay balance, technical stability, or
        the overall design of the Card Game.
      </p>
      <p>
        7.9. The Developers do not guarantee that any particular card, gameplay
        strategy, or in-game item will provide a competitive advantage or lead
        to a specific gameplay outcome.
      </p>
      <p>
        7.10. Gameplay mode allows Users to participate in player-versus-player
        matches involving token-based stakes denominated in TON. The Developers
        do not guarantee that Users will win such matches or recover the tokens
        used to enter such gameplay modes. Users acknowledge that match outcomes
        are uncertain and may result in the loss of tokens used for
        participation.
      </p>
      <p>
        7.11. Any gameplay results, match outcomes, or rewards obtained in the
        Card Game depend on gameplay mechanics, player decisions, and other
        factors that cannot be predicted or guaranteed.
      </p>
      <p>
        7.12. Nothing in these Terms excludes or limits the Developers’
        liability where such limitation is prohibited by applicable law.
      </p>
      <p>
        7.13. The Developers shall not be liable for failures or interruptions
        caused by circumstances beyond their control, including but not limited
        to:
      </p>
      <ul>
        <li>failures or technical changes in the Telegram platform;</li>
        <li>
          failures of external infrastructure, hosting services, or internet
          providers;
        </li>
        <li>cyberattacks, unauthorized access, or malicious software;</li>
        <li>force majeure events.</li>
      </ul>
      <p>
        7.14. In the event of such circumstances, the Developers will make
        reasonable efforts to restore the operation of the Card Game but cannot
        guarantee that the Services will be restored within a specific time
        period.
      </p>
      <p>
        7.15. The Developers may appoint moderators or administrators in
        official communities related to the Card Game. Any opinions, advice, or
        statements made by such moderators or community representatives do not
        constitute official guarantees, commitments, or obligations of the
        Developers.
      </p>

      <h3>8. DATA PROTECTION AND PRIVACY</h3>
      <p>
        8.1. The Developers may collect, process, use, store, and disclose
        personal information when You access or use Platform or any related
        Services.
      </p>
      <p>
        8.2. There are two types of data that You can share with Us when You use
        Our Services:
      </p>
      <ul>
        <li>personal data that You voluntarily share; and</li>
        <li>
          data that is collected automatically by your use of Our Services.
        </li>
      </ul>
      <p>
        8.3. Personal data is processed under{" "}
        <a
          href="https://laws.gov.vg/Laws/data-protection-act-2021"
          target="_blank"
          rel="noopener noreferrer"
        >
          The Data Protection Act, 2021
        </a>{" "}
        and relevant legislation.
      </p>
      <p>8.4. For more detailed information, read the Privacy Policy.</p>
      <p>
        8.5. Where the Card Game is accessed through the game “Labrador
        Adventure”, certain data processing activities may also be subject to
        the Privacy Policy applicable to “Labrador Adventure”, to the extent
        such activities are not governed by this Terms or the Privacy Policy of
        the Card Game.
      </p>

      <h3>9. RISK ACCEPTANCE</h3>
      <p>
        9.1. By using the Card Game and related Services, the User acknowledges
        and accepts the following risks.
      </p>
      <p>
        <b>Volatility of cryptocurrencies.</b> Certain gameplay features of the
        Card Game may involve the use of cryptocurrency denominated in TON. The
        value of cryptocurrencies may be highly volatile and may fluctuate
        significantly over short periods of time due to market conditions,
        demand and supply, regulatory developments, technological factors, or
        other external circumstances. The Developers do not control or influence
        cryptocurrency market prices and are not responsible for any financial
        losses resulting from changes in the value of cryptocurrencies.
      </p>
      <p>
        <b>Gameplay and match outcome risk.</b> Certain gameplay modes may allow
        Users to voluntarily participate in player-versus-player matches
        involving TON-based stakes. Participation in such matches involves the
        risk of losing the tokens used to enter the match. The outcome of
        matches depends on gameplay mechanics, player decisions, and other
        factors, and no outcome or reward is guaranteed.
      </p>
      <p>
        <b>Voluntary spending and entertainment purposes.</b> Users may purchase
        digital items such as cards, cosmetic skins, emoji packs, or other
        in-game items. All such purchases are voluntary and made for
        entertainment purposes only. The Card Game does not guarantee that Users
        will receive any financial return, benefit, or advantage from such
        purchases.
      </p>
      <p>
        <b>Technological and infrastructure risks.</b> The Card Game operates
        through the Telegram platform and may interact with blockchain-based
        systems for certain payments. Users acknowledge that the use of such
        technologies involves technical risks, including but not limited to:
        network congestion, delayed transactions, increased transaction fees,
        blockchain failures, software bugs, internet connectivity issues,
        malicious software, phishing attacks, or unauthorized access to the
        User’s wallet or device.
      </p>
      <p>
        The Developers are not responsible for any losses or interruptions
        caused by such technological risks where they occur outside the
        reasonable control of the Developers.
      </p>
      <p>
        9.2. The User assumes all risks associated with participation in the
        Card Game, including but not limited to the possible loss of funds used
        for gameplay participation, the inability to complete transactions due
        to technical issues, or the inability to use certain features of the
        Card Game.
      </p>
      <p>
        9.3. The User confirms that they have read, understood, and voluntarily
        accepted the risks associated with using the Card Game and related
        Services.
      </p>

      <h3>10. GOVERNING LAW AND DISPUTE RESOLUTION</h3>
      <p>
        10.1 These Terms, along with any disputes or claims arising from or
        relating to their interpretation, enforcement, or the use of the
        Platform, shall be governed by and interpreted in accordance with the
        British Virgin Islands legislation.
      </p>
      <p>
        10.2. Prior to initiating any legal action, both Parties agree to make
        reasonable efforts to resolve disputes through sincere and constructive
        negotiations. Users are encouraged to first reach out to Us to seek a
        resolution.
      </p>
      <p>
        Should the Parties be unable to reach an amicable resolution within
        thirty (30) days, either Party may escalate the matter to the
        appropriate courts.
      </p>
      <p>
        10.3. This clause does not restrict the Developers’s right to pursue
        injunctive or equitable relief, or to enforce its rights, in any
        jurisdiction where the User or their assets may be located.
      </p>

      <h3>11. INTELLECTUAL PROPERTY RIGHTS</h3>
      <p>
        11.1. All Services, as well as all content and materials contained
        therein, including but not limited to: characters, design (including
        architectural and landscape design), text, static and animated graphics,
        images, data, software, sound and music files, visual effects, and any
        other elements and their composition <b>(“Content”)</b> are the property
        of Developers.
      </p>
      <p>
        11.2. The Developers logo, as well as any other products, service names,
        slogans, trademarks and service marks associated with the brand{" "}
        <b>(“Marks”)</b> are Our property, are under Our control or are provided
        to Us under licence. These Marks are protected by copyright and
        trademark laws, as well as other intellectual property regulations and
        unfair competition laws in force in the British Virgin Islands, other
        countries and under international agreements.
      </p>
      <p>
        11.3. Except as expressly permitted by these Terms of Use, no part of
        the Services, Telegram Bot, Content or Marks may be copied, reproduced,
        modified, republished, uploaded, posted, publicly displayed, encrypted,
        translated, transmitted, distributed, sold, licensed or used for
        commercial purposes without Our express prior written consent.
      </p>
      <p>
        11.4. Subject to your continued compliance with these Terms of Use and
        your right to use the Services, You are hereby granted a limited,
        revocable, non-exclusive, non-transferable, non-assignable,
        non-sublicensable right to access and use the Services and Content for
        your personal, non-commercial use.
      </p>
      <p>
        11.5. We reserve all rights not expressly granted in this Terms with
        respect to the Services and all components thereof, including, without
        limitation, the Services, Telegram Bot, Content, and Marks.
      </p>
      <p>
        11.6. If the User provides the Developers with feedback, ideas,
        suggestions for improving the game or any other content (for example, by
        publishing a post or message in the official community), then he/she
        thereby grants the Developers a non-exclusive, perpetual, royalty-free
        right to use, modify, distribute and create derivative works based on
        such content at its own discretion. This is necessary so that the
        Developers can legally implement the ideas received from the community
        or respond to feedback. The User guarantees that he/she has all the
        necessary rights to the content transmitted and that such content does
        not violate the rights of third parties.
      </p>

      <h3>12. THIRD-PARTY WEBSITES</h3>
      <p>
        12.1. When using the Services, You may encounter links to third-party
        websites (<b>hereinafter referred to as “Third-Party Websites”</b>) as
        well as materials such as articles, images, music, videos,
        advertisements, software and other elements created or owned by third
        parties (<b>hereinafter referred to as “Third-Party Content”</b>).
      </p>
      <p>
        12.2. If You click on such a link or interact with Third Party Content,
        We do not notify You that You are leaving Our platform and entering
        another website or service. At that point, the terms of use and privacy
        policy of the third party, for which We are not responsible, will apply
        to You.
      </p>
      <p>
        12.3. Such third-party websites and the content posted on them are not
        under Our control. We do not verify, monitor or guarantee the accuracy,
        timeliness or completeness of their content and, accordingly, are not
        responsible for any websites or materials that You access through Our
        Platform and/or Application.
      </p>
      <p>
        12.4. The presence of a link to a third-party resource or permission to
        use third-party content does not mean that We support, approve or
        recommend it.
      </p>
      <p>
        12.5. If You decide to visit a third-party website or use third-party
        content, You do so at your own risk. In doing so, You should be aware
        that these Terms of Use no longer apply and that the rules and privacy
        policies of the relevant external resource will apply to You.
      </p>
      <p>
        12.6. You acknowledge and agree that We do not endorse or approve of the
        goods or services offered on third-party websites. You also waive any
        claims against Developers and its affiliates that may arise in
        connection with your use of third-party content or your transition to
        third-party resources.
      </p>
      <p>
        12.7. We make no warranties or representations regarding third-party
        content that You may encounter while using the Service, including
        materials related to Digital Assets or Items displayed on the Service.
      </p>
      <p>
        12.8. The Services may contain links, integrations, or references to
        Telegram bots operated by third parties. Such third-party Telegram bots
        are not owned, controlled, or operated by the Developers. The Developers
        are not responsible for the functionality, content, security,
        transactions, data processing, or any actions performed by such
        third-party Telegram bots. Any interaction, transactions, or
        communications between the User and such third-party Telegram bots are
        solely between the User and the respective third party, and the
        Developers shall not be liable for any losses, damages, or claims
        arising from such interactions.
      </p>

      <h3>13. AMENDMENTS AND NOTICES</h3>
      <p>
        13.1. The Developers reserve the right to revise, update, or modify
        these Terms at any time, either partially or in full, to accommodate
        changes in applicable legislation, technological advancements, business
        operations, or others.
      </p>
      <p>
        13.2. Material changes to the Terms will be announced by publication on
        the Platform and/or through official communication channels such as
        email, Telegram or other methods specified by the Developers. Unless
        otherwise specified, such changes will take effect immediately upon
        publication on the Platform, and such publication will be read as
        appropriate to notify Users of the changes.
      </p>
      <p>
        13.3. By continuing to access or use the Services following the
        implementation of updated Terms, the User affirms their acceptance and
        agreement to be legally bound by the revised Terms. If the User
        disagrees with any modifications, they must discontinue use of the
        Services without delay and may request deactivation of their Account and
        associated access rights.
      </p>
      <p>
        13.4. All official notifications from the Developers shall be considered
        effectively delivered when sent to the most recent contact information
        provided by the User, whether during initial onboarding or via later
        updates. Users are solely responsible for maintaining the accuracy and
        completeness of their contact details at all times.
      </p>
      <p>
        13.5. The Developers shall not be held accountable for any failure to
        deliver notices resulting from outdated or incorrect contact
        information.
      </p>
      <p>
        13.6. All notices and communications under these Terms shall be
        conducted in English.
      </p>
      <p>
        13.7. Users acknowledge and agree that it is their personal
        responsibility to regularly review these Terms in order to remain
        informed of the most up-to-date version. While the Developers may
        provide notifications regarding material amendments, the absence of a
        direct notification shall not relieve the User from the obligation to
        comply with the revised Terms. By continuing to access or use the
        Services, the User confirms that they have independently familiarized
        themselves with the current version of the Terms as published on the
        Platform. Failure to periodically review the Terms shall not constitute
        a valid excuse for non-compliance or breach of these Terms.
      </p>

      <h3>13.1. CHANGES AND TERMINATION OF THE PLATFORM</h3>
      <p>
        13.1.1. The User understands and accepts that the Card Game is
        constantly evolving. The Developers reserve the right to change, add, or
        remove gameplay mechanics, cards, abilities, features, store items,
        match rules, or other elements of the Card Game at any time at their
        sole discretion. This may include, but is not limited to, changes to
        gameplay balance (such as card attributes, abilities, rarity, or
        effectiveness), store content, token-based match mechanics, or other
        gameplay parameters. Such changes may be introduced in order to improve
        gameplay, maintain balance, respond to player behavior, enhance the
        technical stability of the Card Game, or comply with legal or regulatory
        requirements.
      </p>
      <p>
        13.1.2. The Developers may release updates, patches, balancing
        adjustments, or technical fixes for the Card Game from time to time. The
        User acknowledges that certain updates may change gameplay mechanics,
        cards, or other features of the Card Game. During the installation of
        updates or technical maintenance, access to the Card Game may be
        temporarily restricted. The Developers will make reasonable efforts to
        inform Users about scheduled maintenance but reserve the right to
        perform urgent technical work without prior notice where necessary.
      </p>
      <p>
        13.1.3. The Developers reserve the right to suspend, modify, or
        completely discontinue the operation of the Card Game (temporarily or
        permanently) at any time and for any reason, including economic
        considerations, legal restrictions, strategic decisions, technical
        limitations, or force majeure circumstances. In the event of a planned
        shutdown of the Card Game, the Developers may notify Users in advance
        through official communication channels where reasonably possible. After
        the termination of the Card Game, Users may lose access to their
        accounts, cards, cosmetics, or other in-game items, and the Developers
        shall not be obligated to preserve or compensate the value of such
        items.
      </p>
      <p>
        13.1.4. The Card Game or its related services may be transferred to
        another legal entity, operator, or development team as part of a merger,
        acquisition, asset sale, or other corporate restructuring. In such a
        case, the rights and obligations of the Developers under these Terms may
        be transferred to the successor entity without the need to obtain the
        User’s additional consent, subject to applicable law. Continued use of
        the Card Game after such transfer will constitute acceptance of the
        successor operator.
      </p>
      <p>
        13.1.5. The Developers reserve the right to moderate, restrict, or
        require the removal of any User-generated content related to the Card
        Game, including content posted in official communities, channels, or
        other communication platforms associated with the Card Game, where such
        content violates these Terms, infringes the rights of third parties,
        spreads false information about the project, or may damage the
        reputation of the Card Game or the Developers. In cases of serious
        violations, the Developers may take additional legal or administrative
        measures to protect their rights.
      </p>

      <h3>14. TERM AND TERMINATION</h3>
      <p>
        14.1. This Terms is a public offer and comes into force from the moment
        of its acceptance by the User.
      </p>
      <p>
        14.2. The Terms are valid indefinitely until its termination by one of
        the Parties in the manner prescribed by these Terms.
      </p>
      <p>
        14.3. The User has the right to refuse to use the Service at any time
        and thereby terminate this Terms, having previously completed all
        unfinished transactions and settled obligations to the Developers.
      </p>
      <p>
        14.4. Users have the option to delete account information at any time
        and thereby terminate the contract with the Developers by contacting Us.
        Upon receiving a deletion request, We will assist Users in the process
        of deleting account information.
      </p>
      <p>
        14.5. The Developers have the right to terminate this Terms unilaterally
        in the following cases:
      </p>
      <ul>
        <li>14.5.1. if the User violates the provisions of this Terms;</li>
        <li>
          14.5.2. if there is a suspicion of illegal or fraudulent activity;
        </li>
        <li>
          14.5.3. if violations of sanctions, currency or tax legislation are
          detected;
        </li>
        <li>
          14.5.4. if it is technically impossible to continue providing
          Services;
        </li>
        <li>14.5.5. if the Service is terminated.</li>
      </ul>

      <h3>15. CONTACT DETAILS</h3>
      <p>
        15.1. If You have any questions about this Terms, please contact us at:{" "}
        <b>support@labrador.team</b>
      </p>
    </div>
  )
}
